import { DEFAULT_VIDEO_CONSTRAINTS, INIT_MEDIA_INFO } from "./constants";

function restartMediaWithNewDevice(room, kind, newDeviceId) {
  const publications = kind === 'audio'
    ? room.localParticipant.audioTracks
    : room.localParticipant.videoTracks;

  let config = {
    deviceId: { exact: newDeviceId },
  };

  if (kind === 'video') {
    config = {...config, ...DEFAULT_VIDEO_CONSTRAINTS};
  }

  const track = publications.values().next().value.track; // Get the first track

  track.restart(config); 
}

/**
 * Switch the source of local video track to a new camera
 * @param {Room} room 
 * @param {Int} cameraId 
 */
export function switchToCamera(room, cameraId) {
  restartMediaWithNewDevice(room, 'video', cameraId);
}

/**
 * Switch the source of local audio track to a new microphone
 * @param {Room} room 
 * @param {Int} micId 
 */
export function switchToMic(room, micId) {
  restartMediaWithNewDevice(room, 'audio', micId);
}

// Copied from twilio official repo https://github.com/twilio/video-quickstart-js.git
/**
 * Mute/unmute your media in a Room.
 * @param {Room} room - The Room you have joined
 * @param {'audio'|'video'} kind - The type of media you want to mute/unmute
 * @param {'mute'|'unmute'} action - Whether you want to mute/unmute
 */
function muteOrUnmuteYourMedia(room, kind, action) {
  const publications = kind === 'audio'
    ? room.localParticipant.audioTracks
    : room.localParticipant.videoTracks;

  publications.forEach(function(publication) {
    if (action === 'mute') {
      publication.track.disable();
    } else {
      publication.track.enable();
    }
  });
}

/**
 * Mute your audio in a Room.
 * @param {Room} room - The Room you have joined
 * @returns {void}
 */
export function muteYourAudio(room) {
  muteOrUnmuteYourMedia(room, 'audio', 'mute');
}

/**
 * Mute your video in a Room.
 * @param {Room} room - The Room you have joined
 * @returns {void}
 */
export function muteYourVideo(room) {
  muteOrUnmuteYourMedia(room, 'video', 'mute');
}

/**
 * Unmute your audio in a Room.
 * @param {Room} room - The Room you have joined
 * @returns {void}
 */
export function unmuteYourAudio(room) {
  muteOrUnmuteYourMedia(room, 'audio', 'unmute');
}

/**
 * Unmute your video in a Room.
 * @param {Room} room - The Room you have joined
 * @returns {void}
 */
export function unmuteYourVideo(room) {
  muteOrUnmuteYourMedia(room, 'video', 'unmute');
}

/**
 * A RemoteParticipant muted or unmuted its media.
 * @param {Room} room - The Room you have joined
 * @param {function} onMutedMedia - Called when a RemoteParticipant muted its media
 * @param {function} onUnmutedMedia - Called when a RemoteParticipant unmuted its media
 * @returns {void}
 */
export function participantMutedOrUnmutedMedia(room, onMutedMedia, onUnmutedMedia) {
  room.on('trackSubscribed', function(track, publication, participant) {
    track.on('disabled', function() {
      return onMutedMedia(track, participant);
    });
    track.on('enabled', function() {
      return onUnmutedMedia(track, participant);
    });
  });
}

/**
 * Check if the MediaDevice api is supported
 * @returns bool: The result of the check
 */
const checkMediaAPISupport = () => {
  if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
    return false;
  } else {
    return true;
  }
}

/**
 * Get the media device information
 * @returns object: The media device information
 */
export async function getMediaDeviceInfo() {
  
  if(!checkMediaAPISupport()) {
    return INIT_MEDIA_INFO;
  }

  const devices = await navigator.mediaDevices.enumerateDevices();

  return {
    audioInputDevices: devices.filter(device => device.kind === 'audioinput'),
    videoInputDevices: devices.filter(device => device.kind === 'videoinput'),
    audioOutputDevices: devices.filter(device => device.kind === 'audiooutput'),
    hasAudioInputDevices: devices.some(device => device.kind === 'audioinput'),
    hasVideoInputDevices: devices.some(device => device.kind === 'videoinput'),
    mediaAPISupported: true,
  };
}

export const initialVCState = {
  activeAudioInputId: window.localStorage.getItem('vcActiveAudioInputId') || 'default',
  activeVideoInputId:  window.localStorage.getItem('vcActiveVideoInputId') || 'default',
  activeAudioOutputId:  window.localStorage.getItem('vcActiveAudioOutputId') || 'default',
}
